'use client';

import { useState, type RefObject } from 'react';

import { Button, toast } from '@backyard-ui/core';

import { ErrorReporter } from '@/infra/monitoring/error-reporter';
import { type Optional } from '@/infra/services/catalog/service/model';
import { ServiceContractAddToCartModal } from '@/presenters/components/common/ServiceContractModal';
import { useAddServicesToCart } from '@/presenters/hooks/checkout/cart';
import { useServiceState } from '@/presenters/store/catalog/service';

import { type OptionalsRef } from '../Optionals';

interface BuyButtonProps {
  selectedOptionals: Optional[];
  optionalsRef: RefObject<OptionalsRef>;
}

function BuyButton(props: Readonly<BuyButtonProps>) {
  const { selectedOptionals, optionalsRef } = props;

  const resetOptinals = () => {
    if (optionalsRef.current) optionalsRef.current.clearAll();
  };

  const [serviceContractModalIsOpen, setServiceContractModalIsOpen] =
    useState(false);

  const { mutate: addServicesToCart, isLoading } = useAddServicesToCart({
    onSuccess: () => {
      toast({
        description: 'Serviço adicionado ao carrinho',
        appearance: 'success',
      });
      resetOptinals();
    },
    onError: (error) => {
      toast({
        title: 'Ops!',
        description:
          'Tivemos um problema ao adicionar seu serviço ao carrinho. Por favor, recarregue a página e tente novamente. ',
        appearance: 'critical',
      });

      ErrorReporter.report(new Error('AddServiceToCartException'), {
        serviceId,
        optionalIds: selectedOptionals.map(({ id }) => id),
      });
    },
  });

  const serviceId = useServiceState((state) => state.service._id);

  const characteristics = useServiceState(
    (state) => state.service.characteristics
  );

  return (
    <>
      <Button
        isFullWidth
        size="md"
        isLoading={isLoading}
        onClick={() => setServiceContractModalIsOpen(true)}
      >
        Quero este serviço
      </Button>
      <ServiceContractAddToCartModal
        characteristics={characteristics}
        isOpen={serviceContractModalIsOpen}
        onOpenChange={setServiceContractModalIsOpen}
        onAddToCart={() =>
          addServicesToCart({
            services: [
              { id: serviceId, quantity: 1 },
              ...selectedOptionals.map(({ id }) => ({ id, quantity: 1 })),
            ],
          })
        }
        isLoading={isLoading}
      />
    </>
  );
}

export default BuyButton;
